import React, { useEffect, useState } from "react";
import {
  Card,
  Text,
  Paper,
  Badge,
  Grid,
  Select,
  Group,
  Button,
  Container,
  Stack,
  Title,
  SimpleGrid,
  Divider,
  Skeleton,
  ThemeIcon,
  Box,
  ScrollArea,
  Loader,
  Space,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import {
  IconFilter,
  IconShoppingCart,
  IconCurrencyRupee,
  IconFileInvoice,
  IconChartBar,
  IconCalendar,
  IconDownload,
} from "@tabler/icons-react";
import {
  getDocs,
  query,
  collection,
  where,
  orderBy,
  documentId,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import BenchTopHeader from "../UIComponent/Components/BenchTopHeader";
import useBranchId from "../../hooks/useBranchId";
import BenchHeading from "../pages/UI/BenchHeading";
import { BenchTable } from "../pages/UI";
import { logError } from "../ErrorLogService/ErrorLoggingService";
import dayjs from "dayjs";
import {
  startOfToday,
  endOfToday,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
} from "date-fns";
import * as XLSX from "xlsx";

const filterOptions = [
  { value: "today", label: "Today" },
  { value: "current_week", label: "Current Week" },
  { value: "previous_week", label: "Previous Week" },
  { value: "current_month", label: "Current Month" },
  { value: "previous_month", label: "Previous Month" },
  { value: "current_year", label: "Current Year" },
  { value: "previous_year", label: "Last Year" },
  { value: "specific_date", label: "Specific Date" },
  { value: "date_range", label: "Date Range" },
];

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D"];

export default function ProductSalesReport() {
  const companyId = useAppSelector((state: RootState) => state.session.company_id);
  const { branchesList } = useAppSelector((state) => state.branchList);

  const [selectedFilter, setSelectedFilter] = useState<string | null>("current_month");
  const { branchId, setBranchId } = useBranchId(branchesList);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [filterDate, setFilterDate] = useState<Date | null>(null);

  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalItems: 0,
    totalInvoices: 0,
    avgOrderValue: 0,
  });
  const [productPerformance, setProductPerformance] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  useEffect(() => {
    if (companyId) {
      if (
        (selectedFilter === "specific_date" && !filterDate) ||
        (selectedFilter === "date_range" && (!fromDate || !toDate))
      ) {
        return;
      }
      fetchSalesReport();
    }
  }, [companyId, branchId, selectedFilter, fromDate, toDate, filterDate]);

  const fetchSalesReport = async () => {
    if (!companyId) return;
    setLoading(true);

    try {
      const isMonthlyOrYearly = [
        "current_month",
        "previous_month",
        "current_year",
        "previous_year",
      ].includes(selectedFilter || "");

      if (isMonthlyOrYearly) {
        await fetchFromProductSummaries();
      } else {
        await fetchFromPaymentHistory();
      }
    } catch (error) {
      logError(error, "fetchSalesReport");
      console.error("Error fetching sales data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFromProductSummaries = async () => {
    const summariesRef = collection(db, "companyprofile", companyId, "productsummaries");
    let baseQuery = query(summariesRef);

    if (branchId && branchId !== "all_branches") {
      baseQuery = query(baseQuery, where("branch_id", "==", branchId));
    }

    const now = new Date();
    let startMonth = -1;
    let startYear = -1;
    let endMonth = -1;
    let endYear = -1;

    switch (selectedFilter) {
      case "current_month":
        startMonth = now.getMonth() + 1;
        startYear = now.getFullYear();
        endMonth = startMonth;
        endYear = startYear;
        break;
      case "previous_month": {
        const prevDate = subMonths(now, 1);
        startMonth = prevDate.getMonth() + 1;
        startYear = prevDate.getFullYear();
        endMonth = startMonth;
        endYear = startYear;
        break;
      }
      case "current_year":
        startMonth = 1;
        startYear = now.getFullYear();
        endMonth = 12;
        endYear = now.getFullYear();
        break;
      case "previous_year":
        startMonth = 1;
        startYear = now.getFullYear() - 1;
        endMonth = 12;
        endYear = now.getFullYear() - 1;
        break;
    }

    const querySnapshot = await getDocs(baseQuery);
    let summaries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));

    if (startYear !== -1 && endYear !== -1) {
      summaries = summaries.filter((s: any) => {
        const sMonth = Number(s.month);
        const sYear = Number(s.year);
        // basic filter for same year or simple range
        if (startYear === endYear) {
          return sYear === startYear && sMonth >= startMonth && sMonth <= endMonth;
        } else {
          return (
            (sYear === startYear && sMonth >= startMonth) ||
            (sYear > startYear && sYear < endYear) ||
            (sYear === endYear && sMonth <= endMonth)
          );
        }
      });
    }

    processSalesData(summaries, false);
  };

  const fetchFromPaymentHistory = async () => {
    let baseQuery = query(collection(db, "paymenthistory"));
    baseQuery = query(baseQuery, where("company_id", "==", companyId));
    baseQuery = query(baseQuery, where("type", "==", "income"));

    if (branchId && branchId !== "all_branches") {
      baseQuery = query(baseQuery, where("branch_id", "==", branchId));
    }

    const now = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (selectedFilter) {
      case "today":
        startDate = startOfToday();
        endDate = endOfToday();
        break;
      case "current_week":
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case "previous_week":
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case "specific_date":
        if (filterDate) {
          startDate = new Date(filterDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(filterDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      case "date_range":
        if (fromDate && toDate) {
          startDate = new Date(fromDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(toDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
    }

    if (startDate && endDate) {
      baseQuery = query(
        baseQuery,
        where("received_date", ">=", startDate),
        where("received_date", "<=", endDate)
      );
    }

    const querySnapshot = await getDocs(baseQuery);
    const records = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));

    // Extract unique invoice IDs to fetch their details
    const invoiceIds = Array.from(new Set(records.map(r => r.invoice_id).filter(Boolean)));
    const invoicesMap: Record<string, any> = {};

    if (invoiceIds.length > 0) {
      // Fetch invoices in batches (Firestore 'in' limit is 30)
      for (let i = 0; i < invoiceIds.length; i += 30) {
        const batchIds = invoiceIds.slice(i, i + 30);
        const invoiceQuery = query(
          collection(db, "invoice"),
          where(documentId(), "in", batchIds)
        );
        const invoiceSnapshot = await getDocs(invoiceQuery);
        invoiceSnapshot.forEach(doc => {
          invoicesMap[doc.id] = doc.data();
        });
      }
    }

    // Attach product information from invoices to the payment records
    // We only attach products to the payment records to let processSalesData handle it
    const enhancedRecords = records.map(record => {
      if (record.invoice_id && invoicesMap[record.invoice_id]) {
        const invoiceData = invoicesMap[record.invoice_id];
        // Convert invoice item_list to the products format expected by processSalesData
        const invoiceProducts = (invoiceData.item_list || []).map((item: any) => ({
          product_id: item.product_id || "unknown",
          product_name: item.product_name || "Unknown Product",
          product_count: Number(item.quantity) || 0,
          amount: Number(item.amount) || 0
        }));

        return {
          ...record,
          products: invoiceProducts
        };
      }
      return record;
    });

    processSalesData(enhancedRecords, true);
  };

  const processSalesData = (dataArray: any[], isPaymentHistory: boolean = false) => {
    const productStats: Record<string, any> = {};
    const trendStats: Record<string, any> = {};
    let totalRev = 0;
    let totalQty = 0;
    let totalTransactionCount = 0;

    const processedInvoices = new Set<string>();

    dataArray.forEach((item) => {
      const branchName = branchesList.find((b: any) => b.id === (item.branch_id || branchId))?.name || "Unknown Branch";

      if (isPaymentHistory) {
        const amount = Number(item.amount) || Number(item.current_pkg_price) || 0;
        totalRev += amount;
        totalTransactionCount += 1;

        let dateObj: Date;
        if (item.received_date?.toDate) {
          dateObj = item.received_date.toDate();
        } else if (typeof item.received_date === 'number') {
          dateObj = new Date(item.received_date * 1000);
        } else if (item.received_date?.seconds) {
          dateObj = new Date(item.received_date.seconds * 1000);
        } else {
          dateObj = new Date(item.received_date || item.payment_date || Date.now());
        }

        const dateKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, '0')}`;

        if (!trendStats[dateKey]) {
          trendStats[dateKey] = { date: dateObj, label: dayjs(dateObj).format("DD MMM YYYY"), revenue: 0, items: 0 };
        }
        trendStats[dateKey].revenue += amount;

        // Use a flag to avoid overcounting products if an invoice has multiple payments in this range
        const shouldCountProducts = !item.invoice_id || !processedInvoices.has(item.invoice_id);
        if (item.invoice_id) processedInvoices.add(item.invoice_id);

        if (shouldCountProducts) {
          if (item.products && Array.isArray(item.products) && item.products.length > 0) {
            item.products.forEach((prod: any) => {
              const pid = prod.product_id || "unknown";
              const bid = item.branch_id || "unknown";
              const key = `${pid}_${bid}`;
              const qty = Number(prod.product_count) || Number(prod.qty) || 1;
              const amt = Number(prod.amount) || Number(prod.totalAmount) || 0;

              totalQty += qty;
              trendStats[dateKey].items += qty;

              if (!productStats[key]) {
                productStats[key] = {
                  name: prod.product_name || prod.name || "Unknown Product",
                  branchName: branchName,
                  qty: 0,
                  revenue: 0,
                };
              }
              productStats[key].qty += qty;
              productStats[key].revenue += amt;
            });
          } else {
            const prodName = item.product_name || item.current_pkg_name || item.standard_package_name || "Income";
            const pid = item.product_id || item.current_pkg_id || "unknown";
            const bid = item.branch_id || "unknown";
            const key = `${pid}_${bid}`;
            const qty = 1;

            totalQty += qty;
            trendStats[dateKey].items += qty;

            if (!productStats[key]) {
              productStats[key] = {
                name: prodName,
                branchName: branchName,
                qty: 0,
                revenue: 0,
              };
            }
            productStats[key].qty += qty;
            productStats[key].revenue += amount;
          }
        }

      } else {
        totalRev += Number(item.total_amount) || 0;
        totalTransactionCount += Number(item.transaction_count) || 0;

        const dateKey = `${item.year}-${String(item.month).padStart(2, "0")}`; // e.g., 2026-03

        if (!trendStats[dateKey]) {
          const tempDate = new Date(Number(item.year), Number(item.month) - 1, 1);
          trendStats[dateKey] = { date: tempDate, label: dayjs(tempDate).format("MMM YYYY"), revenue: 0, items: 0 };
        }
        trendStats[dateKey].revenue += Number(item.total_amount) || 0;

        if (item.products && Array.isArray(item.products)) {
          item.products.forEach((prod: any) => {
            const pid = prod.product_id || "unknown";
            const bid = item.branch_id || "unknown";
            const key = `${pid}_${bid}`;
            const qty = Number(prod.product_count) || 0;
            const amt = Number(prod.amount) || 0;

            totalQty += qty;
            trendStats[dateKey].items += qty;

            if (!productStats[key]) {
              productStats[key] = {
                name: prod.product_name || "Unknown Product",
                branchName: branchName,
                qty: 0,
                revenue: 0,
              };
            }
            productStats[key].qty += qty;
            productStats[key].revenue += amt;
          });
        }
      }
    });

    const perfArray = Object.values(productStats).sort((a: any, b: any) => b.revenue - a.revenue);
    const trendArray = Object.values(trendStats).sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

    const finalTrendArray = trendArray.map((item: any) => ({
      date: item.label,
      revenue: item.revenue,
      items: item.items
    }));

    setSummary({
      totalRevenue: totalRev,
      totalItems: totalQty,
      totalInvoices: totalTransactionCount,
      avgOrderValue: totalTransactionCount > 0 ? totalRev / totalTransactionCount : 0,
    });
    setProductPerformance(perfArray);
    setTrendData(finalTrendArray);
    setSalesData(perfArray);
  };

  const handleDownloadExcel = () => {
    const dataToExport = productPerformance.map((p) => ({
      "Product Name": p.name,
      "Branch Name": p.branchName,
      "Quantity Sold": p.qty,
      "Total Revenue": `₹${p.revenue.toFixed(2)}`,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Product Sales");
    XLSX.writeFile(workbook, `Product_Sales_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const breadcrumbLinks = [{ label: "Product Sales Report" }];

  return (
    <Container size="lg">
       <Box
            sx={(theme) => ({
              position: "sticky",
              top: 110, // Avoid overlapping the fixed navbar
              zIndex: 100,
              backgroundColor: "#FAFAFA",
              paddingTop: theme.spacing.sm,
              //paddingBottom: theme.spacing.xs,
              marginTop: `-${theme.spacing.sm}`,
            })}
          >
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
        <BenchBreadcrumbs links={breadcrumbLinks} />
        <Group position="right">
          <Button
            leftIcon={<IconDownload size={16} />}
            variant="outline"
            onClick={handleDownloadExcel}
            disabled={productPerformance.length === 0}
          >
            Export Report
          </Button>
        </Group>
      </SimpleGrid>

      <Divider my="sm" />
</Box>
      <BenchTopHeader
        icon={<IconShoppingCart />}
        title="Product Sales Report"
        description="Detailed insights into product performance, revenue, and sales trends."
      />

      {/* FILTERS */}
      <Card p="lg" shadow="md" radius="lg" mb="xl" withBorder my="xl">
        <Grid align="flex-end">
          <Grid.Col xs={12} sm={6} md={3}>
            <Select
              label="Branch"
              value={branchId}
              onChange={setBranchId}
              data={[
                // { value: "all_branches", label: "All Branches" },
                ...branchesList.map((b: any) => ({ value: b.id, label: b.name })),
              ]}
              icon={<IconFilter size={16} />}
              withinPortal
            />
          </Grid.Col>
          <Grid.Col xs={12} sm={6} md={3}>
            <Select
              label="Filter By"
              value={selectedFilter}
              onChange={setSelectedFilter}
              data={filterOptions}
              placeholder="Select period"
              withinPortal
            />
          </Grid.Col>
          {selectedFilter === "specific_date" && (
            <Grid.Col xs={12} sm={6} md={3}>
              <DatePickerInput
                label="Select Date"
                value={filterDate}
                onChange={setFilterDate}
                icon={<IconCalendar size={16} />}
                valueFormat="DD MMM YYYY"
                popoverProps={{ withinPortal: true }}
              />
            </Grid.Col>
          )}
          {selectedFilter === "date_range" && (
            <Grid.Col xs={12} sm={6} md={3} sx={{ zIndex: 10 }}>
              <DatePickerInput
                label="From"
                value={fromDate}
                onChange={setFromDate}
                icon={<IconCalendar size={16} />}
                valueFormat="DD MMM YYYY"
                popoverProps={{ withinPortal: true }}
              />
            </Grid.Col>
          )}
          {selectedFilter === "date_range" && (
            <Grid.Col xs={12} sm={6} md={3} sx={{ zIndex: 11 }}>
              <DatePickerInput
                label="To"
                value={toDate}
                onChange={setToDate}
                icon={<IconCalendar size={16} />}
                valueFormat="DD MMM YYYY"
                popoverProps={{ withinPortal: true }}
              />
            </Grid.Col>
          )}

        </Grid>
      </Card>

      {/* SUMMARY CARDS */}
      <SimpleGrid cols={4} breakpoints={[{ maxWidth: "md", cols: 2 }, { maxWidth: "xs", cols: 1 }]} mb="xl">
        <SummaryCard
          title="Total Revenue"
          value={`₹${summary.totalRevenue.toLocaleString()}`}
          icon={<IconCurrencyRupee size={28} />}
          color="blue"
          loading={loading}
        />
        <SummaryCard
          title="Items Sold"
          value={summary.totalItems.toLocaleString()}
          icon={<IconShoppingCart size={28} />}
          color="teal"
          loading={loading}
        />
        <SummaryCard
          title="Total Invoices"
          value={summary.totalInvoices.toLocaleString()}
          icon={<IconFileInvoice size={28} />}
          color="grape"
          loading={loading}
        />
        <SummaryCard
          title="Avg Order Value"
          value={`₹${summary.avgOrderValue.toFixed(2)}`}
          icon={<IconChartBar size={28} />}
          color="orange"
          loading={loading}
        />
      </SimpleGrid>

      {/* CHARTS */}
      <Grid gutter="xl" mb="xl">
        <Grid.Col md={7}>
          <Card p="lg" withBorder radius="md" shadow="sm">
            <BenchHeading title="Sales Trend" subheading="Revenue over time" order={4} fw={600} fz="h1" />
            <Space h="md" />
            {loading ? <Skeleton height={300} radius="md" /> : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#228be6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#228be6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" interval="preserveStartEnd" />
                  <YAxis tickFormatter={(val) => `₹${val}`} />
                  <Tooltip formatter={(val) => `₹${Number(val).toLocaleString()}`} />
                  <Area type="monotone" dataKey="revenue" stroke="#228be6" fillOpacity={1} fill="url(#colorRev)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid.Col>

        <Grid.Col md={5}>
          <Card p="lg" withBorder radius="md" shadow="sm">
            <BenchHeading title="Top 5 Products" subheading="By revenue contribution" order={4} fw={600} fz="h1" />
            <Space h="md" />
            {loading ? <Skeleton height={300} radius="md" /> : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={productPerformance.slice(0, 5)}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    dataKey="revenue"
                    nameKey="name"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {productPerformance.slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => `₹${Number(val).toLocaleString()}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {/* TABLE
      <Card p="lg" withBorder radius="md" shadow="sm" mb="xl">
        <BenchHeading title="Product Performance Detail" subheading="Granular breakdown of product sales" order={4} fw={600} fz="h1" />
        <Space h="md" />
        <ScrollArea>
          <BenchTable>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Branch Name</th>
                <th>Units Sold</th>
                <th>Total Revenue</th>
                <th>Contribution (%)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}><Loader size="sm" mt="md" /></td>
                </tr>
              ) : productPerformance.length > 0 ? (
                productPerformance.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.branchName}</td>
                    <td>{item.qty}</td>
                    <td>₹{item.revenue.toLocaleString()}</td>
                    <td>{((item.revenue / (summary.totalRevenue || 1)) * 100).toFixed(1)}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}>No sales data found for the selected period.</td>
                </tr>
              )}
            </tbody>
          </BenchTable>
        </ScrollArea>
      </Card> */}
      <Space h="xl" />
    </Container>
  );
}

function SummaryCard({ title, value, icon, color, loading }: any) {
  return (
    <Card shadow="md" p="lg" radius="md" withBorder>
      <Group position="apart" align="flex-start" noWrap>
        <div>
          <Text size="xs" color="dimmed" weight={700} transform="uppercase">
            {title}
          </Text>
          {loading ? <Skeleton height={30} width={100} mt="xs" /> : (
            <Title order={3} color={color} mt="xs">
              {value}
            </Title>
          )}
        </div>
        <ThemeIcon size={48} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}
